import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  IClienteBusqueda,
  ITrazabilidadCliente,
  IVision360Data,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { handleAxiosError } from '@/shared';

import {
  USE_MOCK,
  MOCK_CLIENTES_BUSQUEDA,
  MOCK_VISION360,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;
const PROVISIONAMIENTO_CAMBIAR_EQUIPO_URL = `${urlBackend}api/v1/provisionamiento/cambiar-equipo/`;

///* tanStack query
export type BuscarClientesParams = {
  query: string;
  tipo?: string;
  enabled?: boolean;
};

export const useBuscarClientes = ({
  query,
  tipo = 'general',
  enabled = true,
}: BuscarClientesParams) => {
  return useQuery({
    queryKey: ['buscar-clientes', query, tipo],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = MOCK_CLIENTES_BUSQUEDA.filter(
          (c) =>
            c.nombre_completo.toLowerCase().includes(query.toLowerCase()) ||
            c.cedula.includes(query) ||
            c.contrato_codigo?.includes(query) ||
            c.ip.includes(query)
        );
        return mockAxiosResponse(filtered.length > 0 ? filtered : MOCK_CLIENTES_BUSQUEDA);
      }
      return get<IClienteBusqueda[]>(
        `${BASE_URL}clientes/buscar/?q=${encodeURIComponent(query)}&tipo=${tipo}`,
        true
      );
    },
    enabled: enabled && query.length >= 3,
  });
};

///*
//* Migracion al vuelo de UNA cedula/RUC desde Softnet.
//* Lo usa el buscador superior cuando el agente ingresa una cedula
//* completa que no esta en tickets_database: dispara la importacion
//* (misma logica que la tab "Por cedulas/RUC" de Migracion Softnet)
//* y al exito invalida la cache de busqueda para que la siguiente
//* query encuentre al cliente recien migrado.
//*
//* Endpoint: POST /api/v1/gestion-tickets/clientes/buscar-y-migrar
//* Body:     { identificacion: string }   (acepta CI 10 dig o RUC 13 dig,
//*                                          tolera guiones/espacios)
//* Respuestas posibles del backend:
//*   - 200 ok    -> { data: { identificacion, ya_existia, importado, ... } }
//*   - 400 fmt   -> formato invalido
//*   - 404 noexist -> "Cliente no encontrado, verifique los datos..."
//*   - 502       -> Softnet no respondio
//*/
export interface BuscarYMigrarResponse {
  identificacion: string;
  ya_existia: boolean;
  importado: boolean;
  import_resumen?: Record<string, unknown>;
}

export const useImportarClienteDesdeSoftnet = () => {
  const qc = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: async (identificacion: string) => {
      return post<BuscarYMigrarResponse>(
        `${BASE_URL}clientes/buscar-y-migrar/`,
        { identificacion },
        true,
      );
    },
    onSuccess: (res) => {
      const ident = (res?.data as any)?.identificacion as string | undefined;
      const yaExistia = (res?.data as any)?.ya_existia as boolean | undefined;
      // Invalida toda la cache del buscador para que la proxima query
      // que coincida con la cedula traiga ya al cliente desde local.
      qc.invalidateQueries({ queryKey: ['buscar-clientes'] });
      setAlert(
        true,
        yaExistia
          ? `Cliente ${ident ?? ''} ya estaba en el sistema.`
          : `Cliente ${ident ?? ''} importado desde Softnet.`,
        AlertSeverityEnum.success,
      );
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      // El backend devuelve mensajes utiles en error.response.data.message
      const apiMsg = error?.response?.data?.message;
      if (status === 404) {
        // Mensaje pedido explicitamente por producto:
        setAlert(
          true,
          apiMsg || 'Cliente no encontrado, verifique los datos e ingrese nuevamente.',
          AlertSeverityEnum.warning,
        );
        return;
      }
      if (status === 400) {
        setAlert(
          true,
          apiMsg || 'Ingresa una cedula (10 digitos) o un RUC (13 digitos) validos.',
          AlertSeverityEnum.warning,
        );
        return;
      }
      // 502 / 5xx / red caida
      handleAxiosError(
        error,
        apiMsg || 'No se pudo contactar a Softnet. Intenta nuevamente en unos segundos.',
        AlertSeverityEnum.error,
      );
    },
  });
};

export const usePerfilCliente = (
  clienteCodigo: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['perfil-cliente', clienteCodigo],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_VISION360.cliente);
      return get(
        `${BASE_URL}clientes/${clienteCodigo}/perfil-completo/`,
        true
      );
    },
    enabled: enabled && !!clienteCodigo,
  });
};

export const useFetchTrazabilidad = (
  clienteId: number,
  contratoId: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['trazabilidad-cliente', clienteId, contratoId],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_VISION360.trazabilidad);
      return get<ITrazabilidadCliente[]>(
        `${BASE_URL}trazabilidad/?cliente_id=${clienteId}&contrato_id=${contratoId}`,
        true
      );
    },
    enabled: enabled && !!clienteId && !!contratoId,
  });
};

export type CrearTrazabilidadClientePayload = {
  cliente: { id_cliente: string | number };
  contrato: { id_contrato: string | number };
  campo_modificado: string;
  valor_anterior: string;
  valor_nuevo: string;
  tipo_cambio: string;
  categoria_cambio: string;
  detalle?: string | null;
  usuario_id?: number;
  usuario_nombre?: string;
};

export const postCrearTrazabilidadCliente = async (data: CrearTrazabilidadClientePayload) => {
  return post<ITrazabilidadCliente>(`${BASE_URL}trazabilidad/`, data, true);
};

export const useVerificarMasivo = (
  contratoCodigo: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['verificar-masivo', contratoCodigo],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ tiene_masivo: true, masivo: MOCK_VISION360.masivos_activos[0] });
      return get(
        `${BASE_URL}clientes/verificar-masivo/?contrato_codigo=${contratoCodigo}`,
        true
      );
    },
    enabled: enabled && !!contratoCodigo,
  });
};

/**
 * Resuelve, para una identificacion, los id_contrato (UUID) de api_contrato
 * del ERP Django que corresponden a cada contrato local del modulo tickets.
 *
 * Necesario para clientes antiguos donde useFetchContratos (Django directo)
 * por identificacion no encuentra match en api_contrato: el micro hace el
 * lookup adicional via api_preagendamiento.id_instalacion_externa = id_inst
 * (almacenado al sincronizar desde Softnet).
 *
 * Respuesta (snake_case por jackson config del micro):
 *   [
 *     {
 *       tickets_contrato_id, numero_referencia, id_instalacion,
 *       erp_id_contrato, erp_codigo,
 *       fuente: 'DIRECTO' | 'PREAGENDAMIENTO' | 'NO_ENCONTRADO'
 *     }
 *   ]
 */
export type ResolvedErpContratoItem = {
  tickets_contrato_id: string;
  numero_referencia?: string | null;
  id_instalacion?: string | null;
  erp_id_contrato?: string | null;
  erp_codigo?: string | null;
  fuente: 'DIRECTO' | 'PREAGENDAMIENTO' | 'NO_ENCONTRADO';
};

export const useResolverContratosErp = (
  identificacion: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['resolver-contratos-erp', identificacion],
    queryFn: async () =>
      get<ResolvedErpContratoItem[]>(
        `${BASE_URL}contratos/resolver-erp/?identificacion=${encodeURIComponent(identificacion)}`,
        true
      ),
    enabled: enabled && !!identificacion,
    refetchOnWindowFocus: false,
  });
};

/**
 * Status del job de sync que pinta SyncProgressDialog. Shape coincide con
 * lo que devuelve GET /sincronizar-erp/status/{id} del micro.
 */
export type SyncJobStatus = {
  job_id: string;
  tipo: string;
  stage: string;
  total: number;
  processed: number;
  percent: number;
  done: boolean;
  error?: string | null;
  mensajes?: string[];
  resumen?: Record<string, any> | null;
};

/**
 * Hook para disparar la sync de contratos huerfanos hacia el ERP. Flujo:
 *   1. start() -> POST /sincronizar-erp devuelve {job_id}.
 *   2. Se activa el polling de GET /sincronizar-erp/status/{id} cada 1s.
 *   3. Cuando job.done = true, deja de pollear y muestra resumen.
 *
 * El componente caller solo necesita { start, status, isStarting, jobId,
 * resetJob } para wirearlo al SyncProgressDialog.
 */
export const useSincronizarContratosErp = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const [jobId, setJobId] = useState<string | null>(null);

  const startMutation = useMutation({
    mutationFn: async () =>
      post<{ job_id: string }>(`${BASE_URL}contratos/sincronizar-erp`, {}, true),
    onSuccess: (res: any) => {
      const id = res?.data?.job_id;
      if (id) setJobId(id);
    },
    onError: (error) => handleAxiosError(error),
  });

  const statusQuery = useQuery({
    queryKey: ['sync-contratos-erp-status', jobId],
    queryFn: async () =>
      get<SyncJobStatus>(
        `${BASE_URL}contratos/sincronizar-erp/status/${jobId}`,
        true,
      ),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data: any = query.state.data;
      const status = data?.data;
      if (status?.done) return false;
      return 1000;
    },
    refetchOnWindowFocus: false,
  });

  const status: SyncJobStatus | null =
    ((statusQuery.data as any)?.data as SyncJobStatus) ?? null;

  // Cuando termina, invalidamos queries dependientes para que el resto de la
  // app vea los contratos recien sincronizados sin recarga manual.
  useEffect(() => {
    if (status?.done && !status.error) {
      queryClient.invalidateQueries({ queryKey: ['resolver-contratos-erp'] });
      queryClient.invalidateQueries({ queryKey: ['vision-360'] });
      queryClient.invalidateQueries({ queryKey: ['contrato'] });
      const r = status.resumen ?? {};
      setAlert?.(true,
        `Sync OK. Huerfanos: ${r.huerfanos_detectados ?? 0} | Creados: ${r.contratos_creados ?? 0}` +
        ` | Ya existian: ${r.contratos_ya_existian ?? 0} | Clientes: ${r.clientes_creados ?? 0}`,
        AlertSeverityEnum.success);
    }
  }, [status?.done]);

  return {
    start: () => startMutation.mutate(),
    isStarting: startMutation.isPending,
    jobId,
    status,
    isActive: !!jobId && !status?.done,
    resetJob: () => setJobId(null),
  };
};

export const useVision360Cliente = (
  clienteId: string,
  contratoId?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['vision-360', clienteId, contratoId],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_VISION360);
      const params = contratoId ? `?contrato_id=${contratoId}` : '';
      return get<IVision360Data>(
        `${BASE_URL}clientes/${clienteId}/vision-360/${params}`,
        true
      );
    },
    enabled: enabled && !!clienteId,
  });
};

export type ConsultarAprovisionamientoParams = {
  identificacion?: string | null;
  contrato?: string | null;
  id_instalacion?: string | null;
  contrato_eva?: string | null;
};

export const useConsultarAprovisionamiento = (
  params: ConsultarAprovisionamientoParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['aprovisionamiento-cliente', params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          queryParams.append(key, String(value));
        }
      });

      return get<any>(
        `${BASE_URL}clientes/aprovisionamiento?${queryParams.toString()}`,
        true
      );
    },
    enabled: enabled && !!params.identificacion,
    refetchOnWindowFocus: false,
  });
};

export const postCambiarEquipoProvisionamiento = async (data: any) => {
  return post<any>(PROVISIONAMIENTO_CAMBIAR_EQUIPO_URL, data, true);
};

// ---------------------------------------------------------------------------
// Aprovisionamiento: Cancelar (bloquea el contrato) / Desconfigurar (no bloquea)
// ---------------------------------------------------------------------------
export type BajaAprovisionamientoParams = {
  identificacion: string;
  contrato?: string | null;
  id_instalacion?: string | null;
};

const useBajaAprovisionamiento = (accion: 'cancelar' | 'desconfigurar') => {
  const qc = useQueryClient();
  const setAlert = useUiStore((s) => s.setAlert);
  return useMutation({
    mutationFn: async (params: BajaAprovisionamientoParams) =>
      post(`${BASE_URL}clientes/aprovisionamiento/${accion}`, params, true),
    onSuccess: (res: any) => {
      // Refresca Vision 360 (estado superior, sección y trazabilidad).
      qc.invalidateQueries({ queryKey: ['vision-360'] });
      setAlert(
        true,
        res?.message ??
          (accion === 'cancelar'
            ? 'Servicio cancelado correctamente.'
            : 'Aprovisionamiento desconfigurado correctamente.'),
        AlertSeverityEnum.success,
      );
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const apiMsg = error?.response?.data?.message;
      if (status === 404 || status === 400) {
        setAlert(true, apiMsg || 'No se pudo completar la acción.', AlertSeverityEnum.warning);
        return;
      }
      // 502 (API externa caída) / 5xx / red
      handleAxiosError(
        error,
        apiMsg || 'No se pudo notificar a la API externa. No se aplicó ningún cambio; reintenta.',
        AlertSeverityEnum.error,
      );
    },
  });
};

export const useCancelarAprovisionamiento = () => useBajaAprovisionamiento('cancelar');
export const useDesconfigurarAprovisionamiento = () => useBajaAprovisionamiento('desconfigurar');
